// src/pages/mc/MCDemandsPage.tsx
//
// Correção UX v2 (PRINCÍPIO ZERO) — réplica LITERAL do wireframe
// docs/RAIOS-UX-SIDEBAR-SPEC.html aba "05 Demandas":
//   - statgrid: métricas CONSOLIDADAS por status (totais acumulados, não 24h)
//     — wireframe .statgrid/.stat (WfKpi + tons ready/run/wait/...);
//   - dfilters: busca (.search) + selects Negócio · Runtime · Supervisão ·
//     Prioridade + botão "Nova demanda" (.dnewbtn) fechando a linha à DIREITA
//     (posição exata do mockup — C1);
//   - kanban POR STATUS (.kanban/.kcol), 8 colunas Prontas → Canceladas com
//     scroll horizontal; cards .drow reaproveitados (DemandRichCard), com
//     chip de pipeline + "Etapa X/Y" nas em execução (C5);
//   - cards em aguard. aprovação têm ✓/✕/👁 (C2 — DemandRichCard.appr);
//   - clique no card → modal dlgDemand (DemandDetailPanel — C3).
//
// Dados: GET /v1/demands (demands-store, per_page 200) + filtros client-side;
// GET /v1/pipeline-runs pro step badge. Realtime preservado:
// useSupabaseRealtime({ table: 'demands' }) → fetchDemands.

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDemandsStore } from '@/stores/demands-store';
import { useSupabaseRealtime, type RealtimeEventPayload } from '@/hooks/useSupabaseRealtime';
import { useCoalesced } from '@/hooks/useCoalesced';
import { useAuth } from '@/context/AuthContext';
import { api, type Business, type Demand, type ProjectSummary } from '@/lib/api';
import { listPipelineRuns, type PipelineRunSummary } from '@/lib/api-pipelines';

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { WfKpi } from '@/components/wf';
import { Package, RefreshCw, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

import { DemandDetailPanel } from './DemandDetailPanel';
import { DemandRichCard, type DemandStepInfo } from './DemandRichCard';
import { stepInfoFromRun } from './demand-step-info';
import { DemandMobileColumn } from './DemandMobileColumn';
import { NewDemandDialog } from './NewDemandDialog';
import { RealtimeStatusBanner } from '@/components/raios/RealtimeStatusBanner';
import {
  COLUMN_WF_TONE,
  DEMAND_COLUMNS,
  columnIdForStatus,
  PRIORITY_FILTER_ITEMS,
  RUNTIME_FILTER_ITEMS,
  SUPERVISION_FILTER_ITEMS,
} from './demand-constants';

import '@/styles/wf-demands.css';

/* ── Helpers ──────────────────────────────────────────────────────────────── */

// demands.business_id existe no DB (migration 20240101000073) e o GET
// /v1/demands retorna a row inteira (select '*'), mas o type Demand do
// api.ts (intocável nesta frente) não declara o campo. Leitura defensiva.
function demandBusinessId(d: Demand): string | null {
  return (d as Demand & { business_id?: string | null }).business_id ?? null;
}

// DEM-2001: a derivação de "Etapa X/Y" + nome do step + ATOR vive em
// demand-step-info.ts (testável em isolamento). Prefere os campos canônicos
// derivados server-side (current_step_slug/steps_total) e subordina o fallback
// heurístico tasks_done/planned — eliminando o "3/4" e o ator errado.

/* ── Skeleton ─────────────────────────────────────────────────────────────── */

function KanbanSkeleton() {
  return (
    <div className="space-y-6">
      <div className="wf-statgrid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="wf-kpi space-y-2">
            <Skeleton className="w-10 h-6 mx-auto" />
            <Skeleton className="w-16 h-3 mx-auto" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="wf-kcol">
            <Skeleton className="w-16 h-3" />
            {[0, 1].map((j) => (
              <div key={j} className="wf-row space-y-2">
                <Skeleton className="w-3/4 h-4" />
                <Skeleton className="h-5 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

export function MCDemandsPage() {
  const { demands, loading, fetchDemands } = useDemandsStore();
  const { tenant } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchDemands();
  }, [fetchDemands]);

  // Realtime de demands (PERF-02): o payload do evento traz a row INTEIRA —
  // aplica patch local na hora (card muda instantâneo, zero rede) e agenda
  // UM refetch de reconciliação coalescido em 10s. Antes era fetch de ~200KB
  // a CADA evento, sem coalesce — com o realtime revivido (DSH-01), rajadas
  // de eventos viravam refetch contínuo (a "lentidão insana").
  const realtimeFilter = tenant?.id ? `tenant_id=eq.${tenant.id}` : undefined;
  // fetchDemands agora resolve Promise<boolean> (sucesso/falha, consumido pelo
  // refresh manual). O coalesce só reconcilia — ignora o retorno; embrulha num
  // callback void pra casar com a assinatura de useCoalesced.
  const reconcileDemands = useCoalesced(
    useCallback(() => {
      void fetchDemands();
    }, [fetchDemands]),
    10_000,
  );
  const realtimeStatus = useSupabaseRealtime(
    { table: 'demands', filter: realtimeFilter },
    useCallback(
      (payload?: RealtimeEventPayload) => {
        const handled = useDemandsStore.getState().applyRealtimeEvent(payload);
        if (handled) reconcileDemands();
        else void fetchDemands();
      },
      [reconcileDemands, fetchDemands],
    ),
  );

  // Projetos só pra resolver o chip do card quando não há pipeline (filtro de
  // projetos foi REMOVIDO — decisão Breno no wireframe: "filtro de projetos morre").
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  useEffect(() => {
    api.listProjects().then(setProjects).catch(() => {});
  }, []);

  // Negócios pro filtro "Negócio: todos" (DEM-03).
  const [businesses, setBusinesses] = useState<Business[]>([]);
  useEffect(() => {
    api.listBusinesses().then(setBusinesses).catch(() => {});
  }, []);

  // Pipeline runs → nome do step + badge "Etapa X/Y" nos cards em execução
  // (wireframe .sname/.stepb). Realtime: pipeline_runs entrou na publication
  // (2026-06-11) — refetch quando o run avança/regride de step.
  const [runs, setRuns] = useState<PipelineRunSummary[]>([]);
  // DEM-1976: "last known good". O refetch é disparado em rajada pelo realtime
  // de pipeline_runs (a cada transição de step). Se um refetch falhar
  // (timeout/500/backend reiniciando), NÃO zeramos o estado — manter o último
  // valor conhecido evita o flicker dos badges "Etapa X/Y" (que somem de todos
  // os cards quando `runs` vira []). Só o PRIMEIRO load pode aceitar vazio como
  // estado legítimo; num refetch já existe estado bom a preservar.
  const runsLoadedOnceRef = useRef(false);
  const loadRuns = useCallback(() => {
    // AGT-17 (admin): TODOS os runs em modo LIGHT (sem config; etapa derivada
    // no servidor) — cards terminais (finalizada/bloqueada/cancelada) também
    // mostram a etapa onde o pipeline está/parou + X/Y. Payload em KBs.
    listPipelineRuns({ light: true })
      .then((next) => {
        runsLoadedOnceRef.current = true;
        setRuns(next);
      })
      .catch((err) => {
        // DEM-1976: não engolir silenciosamente — registra rastro.
        console.warn('[MCDemandsPage] falha ao recarregar pipeline runs; mantendo last known good', err);
        // Se ainda não houve nenhum load bem-sucedido, o estado vazio é
        // legítimo (não há "good" a preservar). Caso contrário, PRESERVA o
        // último estado válido em vez de zerar (`setRuns([])` removido).
        if (!runsLoadedOnceRef.current) {
          setRuns([]);
        }
      });
  }, []);
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);
  // PERF-02: pipeline_runs muda a cada transição de step (rajadas) — coalesce
  // de 3s absorve a sequência num único reload (~100KB) sem perder o "vivo".
  const coalescedLoadRuns = useCoalesced(loadRuns, 3_000);
  useSupabaseRealtime({ table: 'pipeline_runs', filter: realtimeFilter }, coalescedLoadRuns);
  // 2026-07-07: nem toda transicao de STEP toca a row do run (counters so mudam
  // quando task settle) — durante a transicao o badge "Etapa X/Y" congelava ate
  // o proximo evento de pipeline_runs. pipeline_run_steps esta na publication:
  // assina e reusa o MESMO coalesce (sem firehose novo).
  useSupabaseRealtime({ table: 'pipeline_run_steps', filter: realtimeFilter }, coalescedLoadRuns);

  // 2026-07-07: rede de seguranca — se o canal realtime cair (socket/token),
  // a tela congelava no last-known-good sem NENHUM caminho de refresh. Poll
  // leve de 60s enquanto o canal nao esta 'connected'; canal saudavel = efeito
  // desarmado (custo zero).
  useEffect(() => {
    if (realtimeStatus === 'connected') return;
    const t = window.setInterval(() => {
      void fetchDemands();
      loadRuns();
    }, 60_000);
    return () => window.clearInterval(t);
  }, [realtimeStatus, fetchDemands, loadRuns]);

  const [businessFilter, setBusinessFilter] = useState('all');
  const [runtimeFilter, setRuntimeFilter] = useState('all');
  const [supervisionFilter, setSupervisionFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  // F4b 2026-07-20 (pedido do dono): filtro por USUÁRIO dono/criador da demanda.
  const [userFilter, setUserFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailDemand, setDetailDemand] = useState<Demand | null>(null);

  /* ── Refresh manual (botão no header) ────────────────────────────────────── */
  //
  // DEM-2159: atualização sob clique. Re-consulta o backend (mesmo endpoint de
  // leitura da listagem, via fetchDemands) e re-renderiza a lista SEM reload da
  // página (F5). `refreshing` desabilita o botão + gira o ícone enquanto o fetch
  // roda; falha vira toast de erro sem quebrar a tela (a lista atual é
  // preservada pelo store).
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const ok = await fetchDemands();
      if (!ok) {
        toast({ message: 'Não foi possível atualizar as demandas. Tente de novo.', type: 'error' });
      }
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, fetchDemands, toast]);

  /* ── Filter demands (client-side sobre GET /v1/demands) ─────────────────── */

  // F4b — usuário dono da demanda (assinatura F4 + backfill 07-20 em
  // metadata.creator_context). Opções do dropdown derivadas dos items.
  const demandOwnerUser = (d: Demand): { id: string | null; name: string | null } => {
    const cc = (d.metadata?.['creator_context'] ?? null) as Record<string, unknown> | null;
    const id = typeof cc?.['owner_user_id'] === 'string' ? (cc['owner_user_id'] as string) : null;
    const name = typeof cc?.['owner_user_name'] === 'string' ? (cc['owner_user_name'] as string) : null;
    return { id, name };
  };
  const userOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of demands) {
      const { id, name } = demandOwnerUser(d);
      if (id && name && !map.has(id)) map.set(id, name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demands]);

  const filtered = useMemo(() => {
    return demands
      .filter((d) => {
        if (businessFilter !== 'all' && demandBusinessId(d) !== businessFilter) return false;
        if (runtimeFilter !== 'all' && d.runtime_target !== runtimeFilter) return false;
        if (supervisionFilter !== 'all' && d.supervision_mode !== supervisionFilter) return false;
        if (priorityFilter !== 'all' && d.priority !== priorityFilter) return false;
        if (userFilter !== 'all' && demandOwnerUser(d).id !== userFilter) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const matchTitle = d.title.toLowerCase().includes(q);
          const matchCode = d.code?.toLowerCase().includes(q) ?? false;
          const matchId = d.id.toLowerCase().includes(q);
          if (!matchTitle && !matchCode && !matchId) return false;
        }
        return true;
      })
      // Mais recente primeiro (created_at desc). Backend ja retorna ordenado
      // mas re-ordena defensivo pra cobrir realtime updates fora de ordem.
      .sort((a, b) => {
        const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bT - aT;
      });
  }, [demands, businessFilter, runtimeFilter, supervisionFilter, priorityFilter, userFilter, searchQuery]);

  /* ── Bucket demands into columns (kanban por status) ────────────────────── */

  const columnDemands = useMemo(() => {
    const map: Record<string, Demand[]> = {};
    for (const col of DEMAND_COLUMNS) {
      map[col.id] = filtered.filter((d) => col.statuses.includes(d.status));
    }
    return map;
  }, [filtered]);

  /* ── Step badge por demanda (run mais recente — só em execução) ─────────── */

  const stepByDemandId = useMemo(() => {
    const latestRun = new Map<string, PipelineRunSummary>();
    for (const run of runs) {
      const prev = latestRun.get(run.demand_id);
      if (!prev || new Date(run.started_at).getTime() > new Date(prev.started_at).getTime()) {
        latestRun.set(run.demand_id, run);
      }
    }
    const map: Record<string, DemandStepInfo | null> = {};
    // AGT-17 (admin): etapa pra TODAS as demandas com run (antes só
    // running/finishing) — bloqueada mostra onde parou; finalizada, Y/Y.
    // AGT-18 (admin): EXCETO "Prontas" — a demanda nem entrou no pipeline;
    // nome/contagem de etapa ali não fazem sentido (run velho de redraft etc).
    for (const d of demands) {
      if (columnIdForStatus(d.status) === 'prontas') continue;
      map[d.id] = stepInfoFromRun(latestRun.get(d.id));
    }
    return map;
  }, [runs, demands]);

  /* ── Nome do negócio (business) por demanda — badge linha 3 (DEM-2036) ──── */
  const businessNameById = useMemo(() => {
    const byId = new Map(businesses.map((b) => [b.id, b.name]));
    const map: Record<string, string | null> = {};
    for (const d of demands) {
      const bid = demandBusinessId(d);
      map[d.id] = bid ? (byId.get(bid) ?? null) : null;
    }
    return map;
  }, [businesses, demands]);

  /* ── Métricas CONSOLIDADAS (DEM-02) ─────────────────────────────────────── */
  //
  // Totais ACUMULADOS por status sobre TODO o conjunto retornado (não 24h e
  // não afetado pelos filtros — visão consolidada, como o statgrid do
  // wireframe).

  const consolidated = useMemo(() => {
    const map: Record<string, number> = {};
    for (const col of DEMAND_COLUMNS) map[col.id] = 0;
    for (const d of demands) {
      const col = DEMAND_COLUMNS.find((c) => c.statuses.includes(d.status));
      if (col) map[col.id] = (map[col.id] ?? 0) + 1;
    }
    return map;
  }, [demands]);

  return (
    <>
      <div className="p-4 md:p-6 space-y-4 pt-12">
        <PageHeader
          title="Demandas"
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              loading={refreshing}
              disabled={refreshing}
              aria-label="Atualizar demandas"
              title="Atualizar demandas"
            >
              {!refreshing && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
              Atualizar
            </Button>
          }
        />
        <RealtimeStatusBanner status={realtimeStatus} />

        {loading && demands.length === 0 ? (
          <KanbanSkeleton />
        ) : (
          <>
            {/* Métricas consolidadas no topo (wireframe .statgrid — totais por
                status, NÃO 24h) */}
            <div className="wf-statgrid">
              {DEMAND_COLUMNS.map((col) => (
                <WfKpi
                  key={col.id}
                  tone={COLUMN_WF_TONE[col.id]}
                  value={consolidated[col.id] ?? 0}
                  label={col.title}
                />
              ))}
            </div>

            {/* Filtros (wireframe .dfilters): busca · negócio · runtime ·
                supervisão · prioridade + "Nova demanda" à direita (C1).
                Sem projetos, sem executores (decisão Breno). */}
            <div className="wf-dfilters">
              <div className="wf-search" style={{ flex: 1, minWidth: 170 }}>
                <Search className="ic" aria-hidden="true" />
                <input
                  placeholder="Buscar demandas (código ou título)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Buscar demandas"
                />
              </div>
              <select
                value={businessFilter}
                onChange={(e) => setBusinessFilter(e.target.value)}
                aria-label="Filtrar por negócio"
              >
                <option value="all">Negócio: todos</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <select
                value={runtimeFilter}
                onChange={(e) => setRuntimeFilter(e.target.value)}
                aria-label="Filtrar por runtime"
              >
                {RUNTIME_FILTER_ITEMS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                value={supervisionFilter}
                onChange={(e) => setSupervisionFilter(e.target.value)}
                aria-label="Filtrar por supervisão"
              >
                {SUPERVISION_FILTER_ITEMS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                aria-label="Filtrar por prioridade"
              >
                {PRIORITY_FILTER_ITEMS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {/* F4b — filtro por usuário dono (pedido do dono 2026-07-20). */}
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                aria-label="Filtrar por usuário"
              >
                <option value="all">Usuário: todos</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              {/* .dnewbtn — margin-left:auto fecha a linha à direita (C1) */}
              <NewDemandDialog onCreated={fetchDemands} />
            </div>

            {/* Kanban por STATUS (wireframe .kanban) — Prontas → Canceladas,
                scroll horizontal, cards .drow reaproveitados. */}
            {filtered.length === 0 ? (
              <EmptyState
                icon={Package}
                title="Nenhuma demanda encontrada"
                description="Nenhuma demanda corresponde aos filtros selecionados."
              />
            ) : (
              <>
                {/* Desktop: colunas lado a lado com scroll horizontal */}
                <div className="hidden md:block">
                  <div className="wf-kanban">
                    {DEMAND_COLUMNS.map((col) => {
                      const colDemands = columnDemands[col.id] ?? [];
                      return (
                        <div key={col.id} className="wf-kcol">
                          <div className="wf-kcol-h">
                            <span className={cn('wf-kdot', COLUMN_WF_TONE[col.id])} aria-hidden="true" />
                            {col.kanbanTitle ?? col.title}
                            <span className="wf-kc">{colDemands.length}</span>
                          </div>
                          <div className="wf-kcol-body">
                            {colDemands.length === 0 ? (
                              <p className="text-xs text-raios-muted text-center py-3">Sem demandas</p>
                            ) : (
                              colDemands.map((demand) => (
                                <DemandRichCard
                                  key={demand.id}
                                  demand={demand}
                                  onClick={setDetailDemand}
                                  projects={projects}
                                  step={stepByDemandId[demand.id] ?? null}
                                  businessName={businessNameById[demand.id] ?? null}
                                />
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile: colunas empilhadas colapsáveis */}
                <div className="md:hidden space-y-3">
                  {DEMAND_COLUMNS.map((col, i) => (
                    <DemandMobileColumn
                      key={col.id}
                      column={col}
                      demands={columnDemands[col.id] ?? []}
                      onCardClick={setDetailDemand}
                      defaultOpen={i < 3}
                      projects={projects}
                      stepByDemandId={stepByDemandId}
                      businessNameById={businessNameById}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modal de detalhe (wireframe dlgDemand — abas Detalhes / Métricas / Atividade) */}
      <DemandDetailPanel
        // Demand VIVA: lookup na lista realtime; snapshot do clique é fallback.
        demand={detailDemand ? (demands.find((d) => d.id === detailDemand.id) ?? detailDemand) : null}
        onClose={() => setDetailDemand(null)}
      />
    </>
  );
}
